/** Portable data contracts; the bundled manifest describes this browser's protobuf build. */
import nativeManifest from './generatedAuthoringSchema.json';
import { getAllNodeInfos, isProductionReadyNodeType } from '../nodes/registry';

export interface ProtoFieldSchema {
  type?: 'string' | 'boolean' | 'integer' | 'number' | 'array' | 'object';
  kind?: string;
  classification?: 'configurable' | 'internal' | 'derived' | 'unsupported';
  proto_type?: number;
  $ref?: string;
  properties?: Record<string, ProtoFieldSchema>;
  items?: ProtoFieldSchema;
  additionalProperties?: ProtoFieldSchema | false;
  enum_values?: Record<string, number>;
  oneof?: string;
  key_type?: number;
}
export interface TemplateMapping {
  field_path: string;
  chunk_path: string;
  content_mode: string;
  content_type_path?: string;
}
export interface AuthoringNodeSchema {
  schema_fingerprint: string;
  eligible: boolean;
  creatable?: boolean;
  fields: Record<string, ProtoFieldSchema>;
  $defs: Record<string, ProtoFieldSchema>;
  template_mappings: TemplateMapping[];
}
export interface AuthoringSchema {
  version: 1;
  catalog_hash: string;
  nodes: Record<string, AuthoringNodeSchema>;
}

if (nativeManifest.version !== 1)
  throw new Error('Unsupported bundled authoring schema version.');
const nativeNodeSchemas: Record<string, unknown> = nativeManifest.nodes;
export const nativeAuthoringSchema: AuthoringSchema = {
  ...nativeManifest,
  version: nativeManifest.version,
  // The generator validates these bundled node schemas. JSON imports widen
  // nested literals, so keep their one boundary assertion limited to nodes.
  nodes: nativeNodeSchemas as Record<string, AuthoringNodeSchema>,
};
export function browserAuthoringCapabilities() {
  const nativeTypes = getAllNodeInfos().map((info) => info.protobufNodeDataKey);
  return {
    version: 1,
    node_schema_fingerprints: Object.fromEntries(
      nativeTypes
        .filter((type) => nativeAuthoringSchema.nodes[type])
        .map((type) => [
          type,
          nativeAuthoringSchema.nodes[type].schema_fingerprint,
        ])
    ),
    creatable_node_types: nativeTypes.filter(
      (type) =>
        isProductionReadyNodeType(type) &&
        Boolean(nativeAuthoringSchema.nodes[type])
    ),
    field_value_kinds: [
      'string',
      'number',
      'bool',
      'string_list',
      'variable_ref',
      'team_recipient_list',
      'enum',
      'enum_list',
      'number_list',
      'time_of_day_list',
      'date_range_list',
      'template_chunks',
      'protobuf_json',
    ],
    graph_ops_versions: [2],
  };
}

export function bindAuthoringSchema(
  schema: AuthoringSchema | undefined,
  catalogHash?: string
): AuthoringSchema | undefined {
  if (!schema) return undefined;
  if (
    schema.version !== 1 ||
    !catalogHash ||
    schema.catalog_hash !== catalogHash ||
    JSON.stringify(schema).length > 256 * 1024
  )
    throw new Error(
      'The Copilot authoring schema does not match this draft. Reload the workflow.'
    );
  for (const [type, node] of Object.entries(schema.nodes)) {
    if (
      !nativeAuthoringSchema.nodes[type] ||
      node.schema_fingerprint !==
        nativeAuthoringSchema.nodes[type].schema_fingerprint
    )
      throw new Error(`This browser cannot author ${type}. Reload the page.`);
  }
  return schema;
}

const unsafeKeys = new Set(['__proto__', 'prototype', 'constructor']);
// These native framework messages are written only through the installed,
// compiler-trusted reference/template codecs, not ordinary derived fields.
const frameworkCodecFields: Record<string, ReadonlySet<string>> = {
  'protobuf.logiblocks.VariableRef': new Set([
    'node_id',
    'variable_name',
    'derived_variable_name',
    'source',
  ]),
  'protobuf.logiblocks.TextTemplateChunk': new Set(['text', 'variable']),
};
export function cloneBoundedProtoJson(value: unknown): unknown {
  let count = 0;
  const visit = (item: unknown, depth: number): unknown => {
    if (++count > 8192 || depth > 16)
      throw new Error(
        'Protobuf configuration exceeds its size or depth limit.'
      );
    if (item === null || typeof item === 'string' || typeof item === 'boolean')
      return item;
    if (typeof item === 'number' && Number.isFinite(item)) return item;
    if (Array.isArray(item))
      return item.map((entry) => visit(entry, depth + 1));
    if (item && typeof item === 'object') {
      return Object.fromEntries(
        Object.entries(item).map(([key, entry]) => {
          if (unsafeKeys.has(key))
            throw new Error('Unsafe protobuf field name.');
          return [key, visit(entry, depth + 1)];
        })
      );
    }
    throw new Error('Invalid protobuf JSON value.');
  };
  if (JSON.stringify(value).length > 64 * 1024)
    throw new Error('Protobuf configuration exceeds its size limit.');
  return visit(value, 0);
}

export function fieldSchema(
  schema: ProtoFieldSchema,
  defs: Record<string, ProtoFieldSchema>
): ProtoFieldSchema {
  if (!schema.$ref) return schema;
  const target = defs[schema.$ref.replace(/^#\/\$defs\//, '')];
  if (!target) throw new Error('Unsupported protobuf message schema.');
  return { ...target, ...schema, $ref: undefined };
}

/** Singular messages merge; lists/maps replace; explicit oneof selection removes siblings. */
export function mergeProtoJson(
  current: unknown,
  patch: unknown,
  rawSchema: ProtoFieldSchema,
  defs: Record<string, ProtoFieldSchema>,
  depth = 0
): unknown {
  if (depth > 16)
    throw new Error('Protobuf configuration exceeds its depth limit.');
  if (rawSchema.classification && rawSchema.classification !== 'configurable')
    throw new Error('Cannot configure an internal or derived field.');
  const schema = fieldSchema(rawSchema, defs);
  const frameworkType = rawSchema.$ref?.replace(/^#\/\$defs\//, '');
  const codecFields = frameworkType
    ? frameworkCodecFields[frameworkType]
    : undefined;
  if (schema.enum_values) {
    const value = typeof patch === 'string' ? schema.enum_values[patch] : patch;
    if (
      typeof value !== 'number' ||
      !Object.values(schema.enum_values).includes(value)
    )
      throw new Error('Unknown protobuf enum value.');
    return value;
  }
  if (schema.type === 'array') {
    if (!Array.isArray(patch) || !schema.items)
      throw new Error('Expected a protobuf list.');
    return patch.map((value) =>
      mergeProtoJson(undefined, value, schema.items!, defs, depth + 1)
    );
  }
  if (schema.type === 'object') {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch))
      throw new Error('Expected a protobuf message or map.');
    if (
      schema.additionalProperties &&
      typeof schema.additionalProperties === 'object'
    ) {
      for (const key of Object.keys(patch)) {
        const keyType = schema.key_type;
        if (keyType === 8) {
          if (!['true', 'false'].includes(key))
            throw new Error('Invalid protobuf boolean map key.');
        } else if (typeof keyType === 'number' && keyType !== 9) {
          if (
            !/^(?:0|-?[1-9]\d*)$/.test(key) ||
            !Number.isSafeInteger(Number(key))
          )
            throw new Error('Invalid protobuf integer map key.');
          if (
            [5, 15, 17].includes(keyType) &&
            (Number(key) < -2147483648 || Number(key) > 2147483647)
          )
            throw new Error('Protobuf map key is out of range.');
          if (
            [7, 13].includes(keyType) &&
            (Number(key) < 0 || Number(key) > 4294967295)
          )
            throw new Error('Protobuf map key is out of range.');
          if ([4, 6].includes(keyType) && Number(key) < 0)
            throw new Error('Protobuf map key is out of range.');
        }
      }
      return Object.fromEntries(
        Object.entries(patch).map(([key, value]) => [
          key,
          mergeProtoJson(
            undefined,
            value,
            schema.additionalProperties as ProtoFieldSchema,
            defs,
            depth + 1
          ),
        ])
      );
    }
    const properties = schema.properties ?? {};
    if (
      frameworkType === 'protobuf.logiblocks.TextTemplateChunk' &&
      Object.keys(patch).length !== 1
    )
      throw new Error('A template chunk must select exactly one member.');
    const result = {
      ...(current && typeof current === 'object' && !Array.isArray(current)
        ? current
        : {}),
    } as Record<string, unknown>;
    const selected = new Set<string>();
    for (const [name, value] of Object.entries(patch)) {
      const property = properties[name];
      if (!property || unsafeKeys.has(name))
        throw new Error(`Unknown protobuf field ${name}.`);
      if (codecFields && !codecFields.has(name))
        throw new Error(`Unsupported framework template field ${name}.`);
      if (property.oneof) {
        if (selected.has(property.oneof))
          throw new Error('Conflicting protobuf oneof members.');
        selected.add(property.oneof);
        for (const [sibling, candidate] of Object.entries(properties))
          if (candidate.oneof === property.oneof && sibling !== name)
            delete result[sibling];
      }
      result[name] = mergeProtoJson(
        result[name],
        value,
        codecFields
          ? { ...property, classification: 'configurable' }
          : property,
        defs,
        depth + 1
      );
    }
    return result;
  }
  if (schema.type === 'string' && typeof patch === 'string') return patch;
  if (schema.type === 'boolean' && typeof patch === 'boolean') return patch;
  if (
    (schema.type === 'integer' || schema.type === 'number') &&
    typeof patch === 'number' &&
    Number.isFinite(patch)
  ) {
    if (schema.type === 'integer' && !Number.isSafeInteger(patch))
      throw new Error(
        'Protobuf integer cannot be safely represented by this browser.'
      );
    if (
      [5, 15, 17].includes(schema.proto_type ?? 0) &&
      (patch < -2147483648 || patch > 2147483647)
    )
      throw new Error('Protobuf integer is out of range.');
    if (
      [7, 13].includes(schema.proto_type ?? 0) &&
      (patch < 0 || patch > 4294967295)
    )
      throw new Error('Protobuf integer is out of range.');
    if ([4, 6].includes(schema.proto_type ?? 0) && patch < 0)
      throw new Error('Protobuf integer is out of range.');
    return patch;
  }
  throw new Error('Protobuf value does not match the installed schema.');
}

export function clearProtoPaths(
  value: unknown,
  paths: string[],
  schema: ProtoFieldSchema,
  defs: Record<string, ProtoFieldSchema>
): unknown {
  const copy = cloneBoundedProtoJson(value);
  for (const path of paths) {
    const pieces = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    if (!path || pieces.some((part) => unsafeKeys.has(part) || !part))
      throw new Error('Invalid protobuf clear path.');
    let target = copy as Record<string, unknown>;
    let shape = fieldSchema(schema, defs);
    for (let index = 0; index < pieces.length; index += 1) {
      const piece = pieces[index];
      const arrayIndex = shape.type === 'array' && /^\d+$/.test(piece);
      const property = arrayIndex
        ? shape.items
        : (shape.properties?.[piece] ??
          (typeof shape.additionalProperties === 'object'
            ? shape.additionalProperties
            : undefined));
      if (
        !property ||
        (property.classification && property.classification !== 'configurable')
      )
        throw new Error(
          'Cannot clear an unknown, internal or derived protobuf field.'
        );
      if (index === pieces.length - 1) {
        if (arrayIndex)
          throw new Error('Replace a list to remove its entries.');
        delete target[piece];
        break;
      }
      if (!target[piece] || typeof target[piece] !== 'object')
        throw new Error('Clear path must address a message field.');
      target = target[piece] as Record<string, unknown>;
      shape = fieldSchema(property, defs);
    }
  }
  return copy;
}

/** Reference identity comes from native message types, never a coincidental field name. */
export function resolveProtoReferences(
  value: unknown,
  rawSchema: ProtoFieldSchema,
  defs: Record<string, ProtoFieldSchema>,
  resolve: (reference: Record<string, unknown>) => Record<string, unknown>,
  depth = 0
): unknown {
  if (depth > 16) throw new Error('Protobuf reference depth exceeded.');
  const schema = fieldSchema(rawSchema, defs);
  const codecFields = rawSchema.$ref
    ? frameworkCodecFields[rawSchema.$ref.replace(/^#\/\$defs\//, '')]
    : undefined;
  if (rawSchema.$ref === '#/$defs/protobuf.logiblocks.TextTemplateChunk') {
    if (
      !value ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      Object.keys(value).length !== 1
    )
      throw new Error(
        'A compiled template chunk must select exactly one member.'
      );
    if (
      'text' in value &&
      (typeof value.text !== 'string' || value.text.includes('{{'))
    )
      throw new Error('A compiled template contains uncompiled text.');
  }
  if (rawSchema.$ref === '#/$defs/protobuf.logiblocks.VariableRef') {
    if (!value || typeof value !== 'object' || Array.isArray(value))
      throw new Error('Invalid protobuf variable reference.');
    const validated = mergeProtoJson(undefined, value, rawSchema, defs);
    return resolve(validated as Record<string, unknown>);
  }
  if (Array.isArray(value) && schema.items)
    return value.map((item) =>
      resolveProtoReferences(item, schema.items!, defs, resolve, depth + 1)
    );
  if (value && typeof value === 'object' && !Array.isArray(value))
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        const property =
          schema.properties?.[key] ??
          (typeof schema.additionalProperties === 'object'
            ? schema.additionalProperties
            : undefined);
        return [
          key,
          property
            ? resolveProtoReferences(
                item,
                codecFields?.has(key)
                  ? { ...property, classification: 'configurable' }
                  : property,
                defs,
                resolve,
                depth + 1
              )
            : item,
        ];
      })
    );
  return value;
}
